import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Clock, Globe, Mail, MessageSquare } from 'lucide-react';
import type { Developer } from '@/types';

interface DeveloperVisibilityProps {
  developers: Developer[];
}

export function DeveloperVisibility({ developers }: DeveloperVisibilityProps) {
  return (
    <Card 
      title="Your Development Team" 
      subtitle="Meet the developers working on your projects"
    >
      <div className="space-y-4">
        {developers.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <p>No developers assigned yet</p>
          </div>
        ) : (
          developers.map((dev) => (
            <div 
              key={dev.id}
              className="p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-blue-200 transition-colors"
            >
              <div className="flex items-start gap-3">
                <Avatar 
                  src={dev.avatar} 
                  name={dev.name} 
                  size="lg"
                  status="online"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-slate-900">{dev.name}</h4>
                  <p className="text-sm text-slate-500">{dev.role}</p>
                  
                  <div className="flex flex-wrap gap-1 mt-2">
                    {dev.skills.slice(0, 3).map((skill) => (
                      <Badge key={skill} variant="neutral" size="sm">
                        {skill}
                      </Badge>
                    ))}
                    {dev.skills.length > 3 && (
                      <Badge variant="neutral" size="sm">
                        +{dev.skills.length - 3}
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{dev.workingHours}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      <span>{dev.timezone}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <button className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                    <button className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                      <MessageSquare className="w-4 h-4" />
                      Message
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-100">
          <h5 className="font-semibold text-slate-900 mb-2">Overlap Window</h5>
          <p className="text-sm text-slate-600 mb-3">
            Best time for real-time collaboration
          </p>
          <div className="flex items-center gap-3">
            <div className="flex-1 p-2 bg-white rounded border border-slate-200 text-center">
              <p className="text-xs text-slate-500">Your Time</p>
              <p className="font-semibold text-slate-900">9:00 AM - 6:00 PM</p>
            </div>
            <div className="text-slate-400">↔</div>
            <div className="flex-1 p-2 bg-white rounded border border-slate-200 text-center">
              <p className="text-xs text-slate-500">Team Overlap</p>
              <p className="font-semibold text-slate-900">10:00 AM - 5:00 PM</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
